import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  X, Timer, ChevronLeft, ChevronRight, Sparkles, Trophy, Loader2,
  CheckCircle2, XCircle, ListChecks, Hand, Pencil, RotateCcw,
} from "lucide-react";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";
import QuizPreviewEditor from "@/components/quiz/QuizPreviewEditor";

/* ───────────────────────── helpers ───────────────────────── */
const CATEGORY_META = {
  mcq: { label: "Multiple Choice", desc: "4 options, pick the right one.", icon: ListChecks, color: "#7C3AED" },
  drag_drop: { label: "Drag & Drop", desc: "Drag the tiles into the right zones.", icon: Hand, color: "#0EA5E9" },
  fill_blank: { label: "Fill in the Blank", desc: "Type the answer inside the blank.", icon: Pencil, color: "#10B981" },
};
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/* ───────────────────────── root overlay ───────────────────────── */
export default function QuizOverlay({ open, onClose, chapter, subject, savedQuiz = null }) {
  const [stage, setStage] = useState("pick"); // pick | loading | review | play | done
  const [category, setCategory] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [draft, setDraft] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [attachmentText, setAttachmentText] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (savedQuiz) {
      // Replaying a previously published quiz from the saved list
      setQuiz(savedQuiz);
      setCategory(savedQuiz.category || "mcq");
      setDraft(null); setInstructions(""); setAttachmentText(""); setResult(null);
      setStage("play");
    } else {
      setStage("pick"); setCategory(null); setQuiz(null); setDraft(null); setInstructions(""); setAttachmentText(""); setResult(null);
    }
  }, [open, savedQuiz]);

  const buildContext = () => {
    if (!chapter?.pages) return "";
    return chapter.pages
      .map((p) => `Page ${p.order}: ${(p.ocr_text || p.summary || "").slice(0, 800)}`)
      .join("\n\n")
      .slice(0, 5000);
  };

  const generate = async (cat) => {
    setCategory(cat);
    setStage("loading");
    try {
      const { data } = await api.post("/ai/quiz/interactive", {
        category: cat,
        subject: subject.name,
        topic: chapter.title,
        grade: subject.grade_name || "",
        subject_id: subject.id,
        chapter_id: chapter.id,
        num_questions: cat === "drag_drop" ? 3 : 5,
        difficulty: "medium",
        context: buildContext(),
        instructions,
        attachment_text: attachmentText,
        save: false,
      });
      setDraft(data.quiz);
      setStage("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quiz generation failed");
      setStage("pick");
    }
  };

  const publishDraft = async (edited) => {
    setPublishing(true);
    try {
      const { data } = await api.post("/ai/quiz/save", {
        title: edited.title,
        subject: subject.name,
        topic: chapter.title,
        grade: subject.grade_name || "",
        subject_id: subject.id,
        chapter_id: chapter.id,
        category,
        questions: edited.questions,
      });
      setQuiz(data.quiz);
      setStage("play");
      toast.success("Quiz published — starting now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const handleFinish = (res) => { setResult(res); setStage("done"); };
  const restart = () => { setStage("pick"); setCategory(null); setQuiz(null); setDraft(null); setResult(null); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-[#0A0C10]/95 backdrop-blur-md" data-testid="quiz-overlay">
      <button
        onClick={onClose}
        data-testid="quiz-close"
        className="absolute top-5 right-5 z-10 w-10 h-10 rounded-md bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
        aria-label="Close quiz"
      ><X size={18} /></button>

      <div className="h-full w-full overflow-y-auto flex items-start justify-center py-10 px-6">
        <div className="w-full max-w-5xl">
          <AnimatePresence mode="wait">
            {stage === "pick" && (
              <CategoryPicker
                key="pick" chapter={chapter} onPick={generate}
                instructions={instructions} setInstructions={setInstructions} onAttachment={setAttachmentText}
              />
            )}
            {stage === "loading" && (
              <LoadingShell key="loading" category={category} />
            )}
            {stage === "review" && draft && (
              <motion.div key="review" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                <QuizPreviewEditor draft={draft} publishing={publishing} onPublish={publishDraft} onCancel={restart} dark />
              </motion.div>
            )}
            {stage === "play" && quiz && (
              <PlayShell
                key="play"
                quiz={quiz}
                category={category}
                onFinish={handleFinish}
              />
            )}
            {stage === "done" && result && (
              <ResultShell key="done" result={result} category={category} onRestart={restart} onExit={onClose} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── category picker ───────────────────────── */
function CategoryPicker({ chapter, onPick, instructions, setInstructions, onAttachment }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
      <div className="text-center mb-10">
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-white/60">/ ai quiz</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-white tracking-tighter mt-2">Pick your quiz style</h1>
        <p className="text-white/60 text-sm mt-3">Quiz on <span className="text-white">{chapter.title}</span> · you'll review &amp; edit the draft before it goes live.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {Object.entries(CATEGORY_META).map(([key, m]) => {
          const Icon = m.icon;
          return (
            <button
              key={key}
              onClick={() => onPick(key)}
              data-testid={`quiz-cat-${key}`}
              className="group relative bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/30 rounded-xl p-6 text-left transition-colors"
              style={{ "--accent": m.color }}
            >
              <div
                className="w-12 h-12 rounded-md inline-flex items-center justify-center mb-4"
                style={{ background: `${m.color}22`, color: m.color }}
              ><Icon size={22} /></div>
              <div className="font-display font-bold text-2xl tracking-tighter text-white">{m.label}</div>
              <p className="text-sm text-white/60 mt-1.5">{m.desc}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40 group-hover:text-white transition-colors">
                Start <ChevronRight size={12} />
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-8 max-w-2xl mx-auto bg-white/[0.04] border border-white/10 rounded-xl p-5 space-y-3" data-testid="quiz-guidance-panel">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/50">Optional · guide the AI</div>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={2}
          placeholder="e.g. Focus on definitions only, avoid trick questions, use real-life examples…"
          data-testid="quiz-instructions-input"
          className="w-full bg-white/5 border border-white/15 focus:border-[#7C3AED] rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none resize-none"
        />
        <AttachmentPicker dark compact onChange={onAttachment} />
      </div>
    </motion.div>
  );
}

/* ───────────────────────── loading skeleton ───────────────────────── */
function LoadingShell({ category }) {
  const m = CATEGORY_META[category];
  return (
    <motion.div
      key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="py-24 text-center"
    >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-md mb-6" style={{ background: `${m.color}22`, color: m.color }}>
        <Loader2 size={26} className="animate-spin" />
      </div>
      <div className="font-display font-bold text-3xl text-white tracking-tighter">Designing your {m.label}…</div>
      <p className="text-white/50 mt-2 text-sm">Our AI is reading your chapter pages and crafting questions.</p>
    </motion.div>
  );
}

/* ───────────────────────── play shell ───────────────────────── */
function PlayShell({ quiz, category, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(0);
  const total = quiz.questions.length;
  const q = quiz.questions[idx];

  useEffect(() => { const t = setInterval(() => setTimer((s) => s + 1), 1000); return () => clearInterval(t); }, []);

  const submitAnswer = (ans) => setAnswers((a) => ({ ...a, [idx]: ans }));
  const next = () => idx < total - 1 ? setIdx(idx + 1) : finish();

  const finish = () => {
    let correct = 0;
    quiz.questions.forEach((qq, i) => {
      const a = answers[i];
      if (a == null) return;
      if (category === "mcq" && a === qq.correct_index) correct += 1;
      if (category === "fill_blank") {
        const ans = (qq.answers || []);
        const allMatch = ans.length && ans.every((expected, bi) => {
          const got = (a?.[bi] || "").trim().toLowerCase();
          return got === expected.trim().toLowerCase();
        });
        if (allMatch) correct += 1;
      }
      if (category === "drag_drop" && a && qq.correct) {
        const allMatch = Object.entries(qq.correct).every(([itemId, zoneId]) => a[itemId] === zoneId);
        if (allMatch) correct += 1;
      }
    });
    onFinish({ score: correct, total, time_s: timer });
  };

  return (
    <motion.div
      key="play" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* topbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">
          / {CATEGORY_META[category].label} · question {idx + 1} of {total}
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-md text-white text-sm font-mono" data-testid="quiz-timer">
          <Timer size={14} /> {fmt(timer)}
        </div>
      </div>

      {/* progress */}
      <div className="h-1 w-full bg-white/10 rounded mb-8 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-[#FFD600] via-[#FF7AD9] to-[#7C3AED]"
          initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
        >
          {category === "mcq" && <MCQPlayer q={q} value={answers[idx]} onChange={submitAnswer} />}
          {category === "drag_drop" && <DragDropPlayer q={q} value={answers[idx]} onChange={submitAnswer} />}
          {category === "fill_blank" && <FillBlankPlayer q={q} value={answers[idx]} onChange={submitAnswer} />}
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-between">
        <button
          onClick={() => idx > 0 && setIdx(idx - 1)}
          disabled={idx === 0}
          data-testid="quiz-prev"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors text-sm"
        ><ChevronLeft size={14} /> Previous</button>
        <button
          onClick={next}
          data-testid="quiz-next"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-[#0A0C10] text-sm font-semibold hover:scale-[1.02] transition-transform"
        >
          {idx < total - 1 ? <>Next <ChevronRight size={14} /></> : <>Finish <Trophy size={14} /></>}
        </button>
      </div>
    </motion.div>
  );
}

/* ───────────────────────── MCQ player ───────────────────────── */
function MCQPlayer({ q, value, onChange }) {
  const letters = ["A", "B", "C", "D"];
  return (
    <div className="bg-white rounded-2xl p-8 sm:p-12 min-h-[400px] flex flex-col justify-between" data-testid="mcq-player">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7C3AED] font-bold mb-3">Question</div>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0A0C10] tracking-tighter leading-tight">{q.question}</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-8">
        {(q.options || []).slice(0, 4).map((opt, i) => {
          const active = value === i;
          return (
            <button
              key={i}
              onClick={() => onChange(i)}
              data-testid={`mcq-option-${i}`}
              className={`group flex items-center gap-4 p-4 rounded-xl text-left border-2 transition-all duration-200 ${active
                ? "bg-[#7C3AED] border-[#7C3AED] text-white scale-[1.01]"
                : "bg-white border-neutral-200 hover:border-[#7C3AED] hover:scale-[1.01]"}`}
            >
              <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${active ? "bg-white text-[#7C3AED]" : "bg-[#7C3AED]/10 text-[#7C3AED] group-hover:bg-[#7C3AED] group-hover:text-white"} transition-colors`}>
                {letters[i]}
              </div>
              <div className={`text-base font-medium ${active ? "text-white" : "text-[#0A0C10]"}`}>{opt}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── Drag & Drop player ───────────────────────── */
function DragDropPlayer({ q, value, onChange }) {
  const [dragging, setDragging] = useState(null);
  const placements = value || {}; // { itemId: zoneId }
  const items = q.items || [];
  const zones = q.zones || [];

  const itemsOnZone = (zoneId) =>
    items.filter((it) => placements[it.id] === zoneId);
  const unplaced = items.filter((it) => !placements[it.id]);

  const dropOnZone = (zoneId) => {
    if (!dragging) return;
    const next = { ...placements };
    // remove dragging from any prior zone
    delete next[dragging];
    next[dragging] = zoneId;
    onChange(next);
    setDragging(null);
  };
  const dropOnTray = () => {
    if (!dragging) return;
    const next = { ...placements };
    delete next[dragging];
    onChange(next);
    setDragging(null);
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-10 min-h-[400px]" data-testid="drag-drop-player">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#0EA5E9] font-bold mb-3">Drag & Drop</div>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0A0C10] tracking-tighter mb-6">{q.question}</h2>

      {/* Item tray */}
      <div
        className="bg-[#FBFAF7] rounded-xl p-4 border-2 border-dashed border-neutral-200 mb-6 min-h-[80px]"
        onDragOver={(e) => e.preventDefault()}
        onDrop={dropOnTray}
        data-testid="dnd-tray"
      >
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">Drag from here →</div>
        <div className="flex flex-wrap gap-2">
          {unplaced.length === 0 ? (
            <div className="text-xs text-neutral-400 italic">All tiles placed.</div>
          ) : unplaced.map((it) => (
            <DraggableTile key={it.id} item={it} dragging={dragging === it.id} onDragStart={() => setDragging(it.id)} />
          ))}
        </div>
      </div>

      {/* Zones */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {zones.map((z) => (
          <div
            key={z.id}
            data-testid={`dnd-zone-${z.id}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dropOnZone(z.id)}
            className="bg-gradient-to-br from-[#0EA5E9]/5 to-[#0EA5E9]/0 border-2 border-dashed border-[#0EA5E9]/40 rounded-xl p-4 min-h-[120px]"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#0EA5E9] font-bold mb-2">{z.label}</div>
            <div className="flex flex-wrap gap-2">
              {itemsOnZone(z.id).map((it) => (
                <DraggableTile key={it.id} item={it} placed dragging={dragging === it.id} onDragStart={() => setDragging(it.id)} />
              ))}
              {itemsOnZone(z.id).length === 0 && (
                <div className="text-xs text-neutral-400 italic">Drop here…</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function DraggableTile({ item, placed, dragging, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      data-testid={`dnd-tile-${item.id}`}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm select-none cursor-grab active:cursor-grabbing transition-all duration-200 ${placed
        ? "bg-[#0EA5E9] text-white shadow-md"
        : "bg-[#0A0C10] text-white"} ${dragging ? "opacity-50 scale-95" : "hover:scale-[1.03]"}`}
    >
      <Hand size={12} /> {item.label}
    </div>
  );
}

/* ───────────────────────── Fill in the blank player ───────────────────────── */
function FillBlankPlayer({ q, value, onChange }) {
  // sentence contains {{0}}, {{1}} placeholders
  const parts = useMemo(() => {
    const re = /\{\{(\d+)\}\}/g;
    const result = [];
    let last = 0, m;
    while ((m = re.exec(q.sentence || "")) != null) {
      if (m.index > last) result.push({ type: "text", text: q.sentence.slice(last, m.index) });
      result.push({ type: "blank", idx: Number(m[1]) });
      last = m.index + m[0].length;
    }
    if (last < (q.sentence || "").length) result.push({ type: "text", text: q.sentence.slice(last) });
    return result;
  }, [q.sentence]);

  const update = (i, v) => onChange({ ...(value || {}), [i]: v });

  return (
    <div className="bg-white rounded-2xl p-8 sm:p-12 min-h-[400px] flex flex-col justify-center" data-testid="fill-blank-player">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#10B981] font-bold mb-4">Fill in the blank</div>
      <div className="font-display font-bold text-2xl sm:text-4xl text-[#0A0C10] tracking-tighter leading-snug">
        {parts.map((p, i) => p.type === "text" ? (
          <span key={i}>{p.text}</span>
        ) : (
          <input
            key={i}
            type="text"
            value={value?.[p.idx] || ""}
            onChange={(e) => update(p.idx, e.target.value)}
            placeholder="…"
            data-testid={`fb-input-${p.idx}`}
            className="inline-block mx-1 px-3 py-1 min-w-[120px] sm:min-w-[160px] border-b-4 border-[#10B981] bg-[#10B981]/5 focus:bg-[#10B981]/10 focus:outline-none text-[#10B981] text-center font-semibold text-2xl sm:text-3xl rounded-md transition-colors"
            style={{ width: `${Math.max((value?.[p.idx] || "").length + 4, 8)}ch` }}
          />
        ))}
      </div>
      {q.hint && (
        <div className="mt-8 inline-flex items-center gap-2 text-sm text-neutral-500">
          <Sparkles size={14} className="text-[#10B981]" />
          Hint: {q.hint}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Result shell ───────────────────────── */
function ResultShell({ result, category, onRestart, onExit }) {
  const pct = result.total ? Math.round((result.score / result.total) * 100) : 0;
  const great = pct >= 80;

  // Confetti celebration on perfect / great result
  useEffect(() => {
    if (great) {
      const duration = 1800;
      const end = Date.now() + duration;
      const fire = () => {
        confetti({ particleCount: 60, angle: 60, spread: 70, origin: { x: 0, y: 0.8 }, colors: ["#FFD600", "#FF7AD9", "#7C3AED", "#0EA5E9"] });
        confetti({ particleCount: 60, angle: 120, spread: 70, origin: { x: 1, y: 0.8 }, colors: ["#FFD600", "#FF7AD9", "#7C3AED", "#10B981"] });
        if (Date.now() < end) requestAnimationFrame(fire);
      };
      fire();
      // Trumpet sound (browser TTS celebration cue)
      try {
        const u = new SpeechSynthesisUtterance("Amazing! You scored " + result.score + " out of " + result.total);
        u.rate = 1.05; u.pitch = 1.3;
        window.speechSynthesis.speak(u);
      } catch { /* ignore */ }
    }
  }, [great, result.score, result.total]);

  return (
    <motion.div
      key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
      transition={{ type: "spring", stiffness: 200, damping: 22 }}
      className="text-center py-16"
    >
      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#FFD600] via-[#FF7AD9] to-[#7C3AED] shadow-[0_0_60px_-8px_rgba(255,214,0,0.6)] mb-6">
        {great ? <Trophy size={44} className="text-white" /> : <CheckCircle2 size={44} className="text-white" />}
      </div>
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-white/50">/ {CATEGORY_META[category].label}</div>
      <h2 className="font-display font-bold text-5xl sm:text-6xl text-white tracking-tighter mt-3">{result.score} / {result.total}</h2>
      <p className="text-white/70 mt-3 text-lg">{great ? "Amazing work! 🎉" : "Nice try! Keep practicing."}</p>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/40 mt-2">Time · {fmt(result.time_s)}</div>

      <div className="mt-10 inline-flex gap-3">
        <button
          onClick={onRestart}
          data-testid="quiz-restart"
          className="px-5 py-2.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
        ><RotateCcw size={14} /> New quiz</button>
        <button
          onClick={onExit}
          data-testid="quiz-exit"
          className="px-5 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-semibold inline-flex items-center gap-2 hover:scale-[1.02] transition-transform"
        >Close <X size={14} /></button>
      </div>
    </motion.div>
  );
}
